import { Badge, Button, Group, Tabs, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { useLocationId } from '../hooks/useSeedLocation';
import { useTabInUrl } from '../hooks/useTabInUrl';
import { enumLabel, enumOptions } from '../lib/enums';
import { fromIsoDate } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';
import { BenefitsPanel } from './payroll/BenefitsPanel';
import { HoursApproval } from './payroll/HoursApproval';
import { PayrollRulesPanel } from './payroll/PayrollRulesPanel';
import { PeriodHoursGrid } from './payroll/PeriodHoursGrid';
import { WorkingWeeksPanel } from './payroll/WorkingWeeksPanel';

interface PayrollRun {
  id: string;
  payrollPeriodId: string;
  status: string;
  skipped?: { staffId: string }[];
}

interface PayrollPeriod {
  id: string;
  cadence: string;
  periodStart: string;
  periodEnd: string;
}

const PERIODS_PATH = '/admin/v1/payroll/periods';

/** A calendar date shifted by whole days, kept in the "YYYY-MM-DD" the API stores. */
function addDays(date: string, days: number): string {
  // Noon, never midnight: a date-only string parsed as UTC midnight is the previous day
  // everywhere west of Greenwich, which is to say everywhere in Honduras.
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The dates the next pay period would run, worked out from the last one.
 *
 * A manager was being asked to type both ends of every fortnight, forever, from memory. Two ways
 * that goes wrong and neither announces itself: a day left between periods is a day whose hours
 * belong to no register and are never paid, and a day counted in both is a day paid twice. The
 * form now opens on the period that follows the last one, which is what it was always going to be.
 */
function nextPeriodDates(last: PayrollPeriod): { periodStart: string; periodEnd: string } {
  const periodStart = addDays(last.periodEnd, 1);
  const start = new Date(`${periodStart}T12:00:00`);
  const pad = (n: number) => String(n).padStart(2, '0');
  const endOfMonth = () => {
    const d = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  if (last.cadence === 'biweekly') return { periodStart, periodEnd: addDays(periodStart, 13) };
  if (last.cadence === 'semiMonthly') {
    // The two halves of a month, whatever length the month happens to be.
    const periodEnd =
      start.getDate() <= 15 ? `${start.getFullYear()}-${pad(start.getMonth() + 1)}-15` : endOfMonth();
    return { periodStart, periodEnd };
  }
  return { periodStart, periodEnd: endOfMonth() };
}

/**
 * How long a period of each cadence may run before the dates and the label disagree about what is
 * being paid.
 *
 * These bounds are wide on purpose. A first period can be short because the business started in the
 * middle of a month, and a fortnight either side of a public holiday gets moved — neither is a
 * mistake and neither should be refused. What is caught is the one shape that costs money and
 * cannot have been meant: a whole month labelled as one of its halves, or a half-month labelled as
 * a whole one.
 */
const PERIOD_DAYS: Record<string, { max: number; min: number }> = {
  monthly: { min: 20, max: 40 },
  semiMonthly: { min: 1, max: 20 },
  biweekly: { min: 1, max: 20 },
};

/** Whole days from one ISO date to another, both ends counted, or null if either will not parse. */
function daysBetween(from: unknown, to: unknown): number | null {
  const a = fromIsoDate(typeof from === 'string' ? from : null);
  const b = fromIsoDate(typeof to === 'string' ? to : null);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;
}

/**
 * Whether the dates typed can be paid under the cadence chosen.
 *
 * **A period's salary comes from its cadence, not from its length.** The register divides the year
 * into twelve, twenty-four or twenty-six equal payments and hands over one of them, so the dates
 * decide only whose hours are counted and who was employed — a "twice a month" period covering the
 * whole of August pays half of August's salary to every salaried person, and the register says
 * "L 8,000.00 this period" beside dates reading Aug 1 – Aug 31 without either figure looking wrong.
 *
 * It is easy to reach: this form suggests dates from the LAST period's cadence, so the first time
 * anybody moves from monthly to twice-a-month the dates on screen still describe a whole month, and
 * the only thing that changed is the word above them.
 */
function cadenceLengthError(values: Record<string, unknown>): string | null {
  const cadence = typeof values.cadence === 'string' ? values.cadence : '';
  const bounds = PERIOD_DAYS[cadence];
  if (!bounds) return null;
  const days = daysBetween(values.periodStart, values.periodEnd);
  if (days === null || days <= 0) return null;
  if (days > bounds.max) {
    return `${enumLabel('payrollCadence', cadence)} pays one share of the year's salary per period, so these ${days} days would pay a fraction of what they cover. Shorten the period, or set "How often you pay" to Monthly.`;
  }
  if (days < bounds.min) {
    return `${enumLabel('payrollCadence', cadence)} pays a whole month's salary, and these dates cover ${days} days. Extend the period, or set "How often you pay" to how you actually pay.`;
  }
  return null;
}

/** Opens the register a period produced, or generates it if there is not one yet. */
function RegisterAction({ periodId }: { periodId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const runs = useQuery({
    queryKey: listKey('/admin/v1/payroll/runs', { payrollPeriodId: periodId }),
    queryFn: () => rawApi<PayrollRun[]>('GET', `/admin/v1/payroll/runs?payrollPeriodId=${periodId}`),
  });
  const existing = (runs.data ?? [])[0];

  const generate = useMutation({
    mutationFn: () =>
      rawApi<{ run: PayrollRun; lines: unknown[] }>('POST', `/admin/v1/payroll/periods/${periodId}/runs`, {}),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: listKey('/admin/v1/payroll/runs', { payrollPeriodId: periodId }),
      });
      notifyDone(
        'Register ready',
        `${result.lines.length} ${result.lines.length === 1 ? 'person' : 'people'} included.`,
      );
      // Straight to the figures. The previous version reported a line count in a toast and then
      // offered nowhere to go, so the register was unreachable the moment it faded.
      void navigate(`/payroll/runs/${result.run.id}`);
    },
    onError: (err) => notifyError(err, 'generate this register'),
  });

  return (
    <Group gap="xs" wrap="nowrap">
      {/*
        Where the register got to, which is the only state a pay period really has. The column
        this replaces read the period's own status through the run status wording, and every
        period ever created says "Open" there — nothing in the system ever closes one. So the
        table answered a question nobody asked, identically on every row, while the answer people
        came for ("has this fortnight been paid?") was hidden inside a button's label.
      */}
      {existing && (
        <Badge
          variant="light"
          color={existing.status === 'draft' ? 'yellow' : existing.status === 'finalized' ? 'blue' : 'teal'}
        >
          {enumLabel('payrollRunStatus', existing.status)}
        </Badge>
      )}
      {existing && (
        <Button size="xs" variant="light" component={Link} to={`/payroll/runs/${existing.id}`}>
          Open register
        </Button>
      )}
      {/*
        Rebuild disappears once the register has been signed off, because from that point the server
        refuses it — a button whose only outcome is an error teaches an owner that the screen is
        broken. It is the same rule the register page states, said here where the button is.
      */}
      {(!existing || existing.status === 'draft') && (
        <Button
          size="xs"
          variant={existing ? 'subtle' : 'light'}
          loading={generate.isPending}
          onClick={() => generate.mutate()}
        >
          {existing ? 'Rebuild' : 'Build register'}
        </Button>
      )}
    </Group>
  );
}

export function PayrollPage() {
  const locationId = useLocationId();
  // In the address, so a link can land on the tab that holds what it is about rather than on
  // whichever one happens to be first.
  const [tab, setTab] = useTabInUrl(
    ['approve', 'hours', 'weeks', 'periods', 'benefits', 'rules'] as const,
    'approve',
  );

  // The same request the table below makes, under the same key, so this costs nothing beyond
  // reading it. Newest first, which is what the endpoint promises.
  const periods = useQuery({
    queryKey: listKey(PERIODS_PATH),
    queryFn: () => rawApi<PayrollPeriod[]>('GET', PERIODS_PATH),
  });
  const lastPeriod = periods.data?.[0];
  const suggested = lastPeriod ? nextPeriodDates(lastPeriod) : null;

  return (
    <>
      <Title order={3} mb="md">
        Time &amp; pay
      </Title>
      {/*
        Approving hours opens first because it is the job someone comes here to do — every fortnight,
        for every person. Pay periods are set up once and then only revisited to produce the register.

        Entering hours sits directly beside approving them, because it is the same fortnight and the
        same person doing both: what the days were, then whether they are right. The staff here do
        not reliably punch, so on this site typing them in is not the exception it would be
        elsewhere — and before it had a tab, the only door was a dialog on one person's one day.

        Working weeks sits after those two, beside the hours it decides. It was reachable only from a
        section of one person's staff page, which is why the owner's report was that he could not set
        up the schedule at all: everything a fortnight's approval measures against was behind a name
        he had to think to click. The editor is the same one his staff page opens.
      */}
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="approve">Approve hours</Tabs.Tab>
          <Tabs.Tab value="hours">Enter hours</Tabs.Tab>
          <Tabs.Tab value="weeks">Working weeks</Tabs.Tab>
          <Tabs.Tab value="periods">Pay periods</Tabs.Tab>
          <Tabs.Tab value="benefits">Vacation &amp; benefits</Tabs.Tab>
          <Tabs.Tab value="rules">Working rules &amp; rates</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="approve">
          <HoursApproval />
        </Tabs.Panel>

        <Tabs.Panel value="hours">
          <PeriodHoursGrid />
        </Tabs.Panel>

        <Tabs.Panel value="weeks">
          <WorkingWeeksPanel />
        </Tabs.Panel>

        <Tabs.Panel value="periods">
          <CrudSection
            title="Pay periods"
            entityNoun="pay period"
            description="Building a register works out what each person earned, ready for the accountant. Breaks are unpaid, and a salary is prorated for anyone who started or left partway through."
            listPath={PERIODS_PATH}
            defaults={{ locationId }}
            rowLabel={(r) => `the period from ${String(r.periodStart)}`}
            emptyMessage="No pay periods yet. Add one covering the dates you pay for."
            columns={[
              { accessor: 'periodStart', title: 'From', unit: 'date' },
              { accessor: 'periodEnd', title: 'To', unit: 'date' },
              { accessor: 'cadence', title: 'How often', enumGroup: 'payrollCadence' },
            ]}
            rowActions={(row) => <RegisterAction periodId={String(row.id)} />}
            /*
             * Opens on the period that follows the last one. The cadence carries over because a
             * business changes how often it pays roughly never, and the dates are computed from
             * it — still editable, because the one time it does change is the time this would
             * otherwise be wrong.
             */
            fields={[
              {
                name: 'cadence',
                label: 'How often you pay',
                type: 'select',
                required: true,
                options: enumOptions('payrollCadence'),
                defaultValue: lastPeriod?.cadence,
              },
              {
                name: 'periodStart',
                label: 'First day',
                type: 'date',
                required: true,
                defaultValue: suggested?.periodStart,
                description: lastPeriod
                  ? 'Starts the day after the last period ended, so no day is missed or paid twice'
                  : undefined,
              },
              {
                name: 'periodEnd',
                label: 'Last day',
                type: 'date',
                required: true,
                defaultValue: suggested?.periodEnd,
                // On the last day rather than on the cadence, because the last day is the field a
                // person is looking at when the two stop agreeing, and it is the one they will fix.
                validate: (_value, values) => cadenceLengthError(values),
              },
            ]}
          />
        </Tabs.Panel>

        {/*
          What used to be here instead:

          A "Pay rates" tab listing every dated pay record ever written, for everybody, interleaved
          by date — so one person's four raises sat between another person's two, and the screen
          could not answer the only question anyone asks it, which is what a given person is paid
          now. That answer is on the staff list, next to their name, and changing it belongs on their
          own page where their history is.

          A "Clock records" tab listing the last five hundred raw punches across all staff. Nobody
          reads punches; they fix one, for one person, on one day. Correcting a missed punch now sits
          on that person's page, reached from the flag on the hours screen that sent you looking.
        */}

        <Tabs.Panel value="benefits">
          <BenefitsPanel />
        </Tabs.Panel>
        <Tabs.Panel value="rules">
          <PayrollRulesPanel />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
